// src/pages/Questionnaire.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tooltip,
  Zoom,
  Divider,
  IconButton,
  Popover,
  Fade,
} from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { request } from "../utils/request";

export default function Questionnaire() {
  const [data, setData] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverContent, setPopoverContent] = useState("");

  const questions = [
    {
      key: "osVersion",
      label: "1. OS Version",
      logic:
        "Latest OS build and within 60 days of recent security updates is considered secure. Older builds reduce compliance.",
    },
    {
      key: "securityPatches",
      label: "2. Security Patches",
      logic:
        "All critical and cumulative patches must be applied. Missing updates increase risk of known exploit exposure.",
    },
    {
      key: "firewall",
      label: "3. Firewall",
      logic:
        "All profiles (Domain, Private, Public) should be enabled. Disabled firewall layers create network vulnerability.",
    },
    {
      key: "antivirus",
      label: "4. Antivirus",
      logic:
        "A registered and updated antivirus must be installed and active. Outdated or missing antivirus increases malware risk.",
    },
    {
      key: "realTimeProtection",
      label: "5. Real-Time Protection",
      logic:
        "Real-time scanning should be active to detect threats immediately. If off, malware may go undetected until manual scans.",
    },
    {
      key: "diskEncrypted",
      label: "6. Disk Encrypted",
      logic:
        "Full-disk encryption (e.g., BitLocker) ensures data protection in case of theft. Unencrypted drives expose sensitive data.",
    },
    {
      key: "secureBootEnabled",
      label: "7. Secure Boot Enabled",
      logic:
        "Secure Boot prevents boot-time rootkits and firmware tampering. Must be enabled in BIOS/UEFI.",
    },
    {
      key: "trustedCAs",
      label: "8. Trusted CAs",
      logic:
        "Only trusted root certificates should exist. Presence of self-signed or interception CAs (e.g., proxy CA) reduces integrity.",
    },
    {
      key: "usbDevices",
      label: "9. USB Devices",
      logic:
        "No unauthorized removable drives should be connected. Restricting external media reduces risk of malware injection.",
    },
    {
      key: "networkPrivilege",
      label: "10. Network & Privilege",
      logic:
        "User accounts should operate with least privilege (non-admin). Admin rights should be limited to IT personnel only.",
    },
  ];

  // ---------- FETCH ----------
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await request("questionnaire", { method: "GET" });
      const entry = Array.isArray(res) ? res[0] : res;
      setData(entry || {});
      setEditedData(entry || {});
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ---------- HANDLERS ----------
  const handleToggle = (key, value) => {
    setEditedData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const method = data && data._id ? "PUT" : "POST";
      const url =
        data && data._id ? `questionnaire/${data._id}` : "questionnaire";
      await request(url, {
        method,
        body: JSON.stringify(editedData),
        headers: { "Content-Type": "application/json" },
      });
      setConfirmOpen(false);
      fetchData();
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleInfoClick = (event, logic) => {
    setAnchorEl(event.currentTarget);
    setPopoverContent(logic);
  };

  const handleInfoClose = () => setAnchorEl(null);

  const open = Boolean(anchorEl);

  const unsavedChanges = useMemo(() => {
    if (!data) return false;
    return questions.some((q) => data[q.key] !== editedData[q.key]);
  }, [data, editedData]);

  const progress = useMemo(() => {
    const answered = questions.filter((q) => editedData[q.key]).length;
    return Math.round((answered / questions.length) * 100);
  }, [editedData]);

  // ---------- RENDER ----------
  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={4} sx={{ backgroundColor: "#fafafa", borderRadius: 2 }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2.5}
      >
        <Typography variant="h5" fontWeight={600}>
          Compliance Questionnaire
        </Typography>
        {unsavedChanges && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={() => setConfirmOpen(true)}
            sx={{ textTransform: "none", borderRadius: 2, px: 2 }}
          >
            Save Changes
          </Button>
        )}
      </Stack>

      {/* Description */}
      <Typography variant="body1" color="text.secondary" mb={3}>
        Each control contributes equally (10 points) to your overall risk score.
        Review and toggle each according to your system configuration. Click{" "}
        <InfoOutlinedIcon
          fontSize="small"
          sx={{ verticalAlign: "middle", mb: "3px" }}
        />{" "}
        for detailed evaluation logic.
      </Typography>

      {/* Card */}
      <Paper
        elevation={4}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          p: 2.5,
          boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
        }}
      >
        <Box mb={2}>
          <Typography
            variant="body2"
            color="textSecondary"
            fontWeight={500}
            mb={0.7}
          >
            Completion Progress
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            color={
              progress < 50 ? "error" : progress < 80 ? "warning" : "success"
            }
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: "#e0e0e0",
              mb: 1,
            }}
          />
          <Typography variant="caption" color="textSecondary">
            {progress}% complete
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <TableContainer>
          <Table
            size="small"
            sx={{
              borderCollapse: "separate",
              borderSpacing: "0 6px",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#f5f7fb",
                  "& th": { fontWeight: 600, color: "#37474f" },
                }}
              >
                <TableCell sx={{ pl: 3 }}>Control Item</TableCell>
                <TableCell align="left" sx={{ pr: 3, width: 180 }}>
                  Status
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {questions.map((q) => {
                const val = editedData[q.key];
                const isYes = String(val).toUpperCase() === "YES";
                const changed = data && val !== data[q.key];

                return (
                  <TableRow
                    key={q.key}
                    hover
                    sx={{
                      backgroundColor: changed ? "#fffde7" : "#fff",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      borderRadius: 2,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        py: 1.5,
                        pl: 3,
                        pr: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        borderBottom: "none",
                      }}
                    >
                      <Typography variant="body2" sx={{ display: "inline-block", mr: 1 }}>
                        {q.label}
                      </Typography>

                      <Tooltip title="View evaluation logic">
                        <IconButton
                          size="small"
                          onClick={(e) => handleInfoClick(e, q.logic)}
                          sx={{ p: 0.5, verticalAlign: "middle" }}
                        >
                          <InfoOutlinedIcon fontSize="small" sx={{ color: "#607d8b" }} />
                        </IconButton>
                      </Tooltip>

                    </TableCell>


                    <TableCell
                      align="left"
                      sx={{
                        py: 1.5,
                        pl: 1,
                        pr: 2,
                        borderBottom: "none",
                      }}
                    >
                      <Stack direction="row" spacing={1.2} alignItems="center">
                        <Tooltip title="Mark as YES">
                          <IconButton
                            size="small"
                            color="success"
                            sx={{
                              border: isYes
                                ? "2px solid #2e7d32"
                                : "1px solid #cfd8dc",
                              backgroundColor: isYes
                                ? "rgba(46,125,50,0.15)"
                                : "transparent",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: "rgba(46,125,50,0.25)",
                              },
                            }}
                            onClick={() => handleToggle(q.key, "YES")}
                          >
                            <DoneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Mark as NO">
                          <IconButton
                            size="small"
                            color="error"
                            sx={{
                              border: !isYes
                                ? "2px solid #d32f2f"
                                : "1px solid #cfd8dc",
                              backgroundColor: !isYes
                                ? "rgba(211,47,47,0.15)"
                                : "transparent",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: "rgba(211,47,47,0.25)",
                              },
                            }}
                            onClick={() => handleToggle(q.key, "NO")}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Info Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        TransitionComponent={Fade}
        transitionDuration={250}
      >
        <Box p={2} maxWidth={340} sx={{ backgroundColor: "#fff" }}>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            gutterBottom
            color="primary"
          >
            Evaluation Logic
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
            {popoverContent}
          </Typography>
        </Box>
      </Popover>

      {/* ----------- CONFIRMATION DIALOG ----------- */}
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Zoom}
      >
        <DialogTitle fontWeight={600}>Confirm Save</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" lineHeight={1.5}>
            You’ve modified one or more responses. Do you want to save these
            updates to the database?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ borderRadius: 2, textTransform: "none" }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
